import { FiX, FiUsers } from "react-icons/fi";
import { ExerciseInPlan } from "../../../../../types";

interface ExerciseViewRowProps {
  readonly exercise: ExerciseInPlan;
  readonly originalExercise?: ExerciseInPlan;
  readonly onNotRecommend?: (exerciseId: number, forAll?: boolean) => void;
  readonly showNotRecommendButton?: boolean;
  readonly allowForAll?: boolean;
}

export function ExerciseViewRow({
  exercise,
  originalExercise,
  onNotRecommend,
  showNotRecommendButton = false,
  allowForAll = false,
}: ExerciseViewRowProps) {
  const hasOriginal = !!originalExercise;
  const hasChanges =
    hasOriginal &&
    (originalExercise.name !== exercise.name ||
      (originalExercise.sets || originalExercise.series) !==
        (exercise.sets || exercise.series) ||
      (originalExercise.reps || originalExercise.repetitions) !==
        (exercise.reps || exercise.repetitions) ||
      originalExercise.load !== exercise.load ||
      originalExercise.interval !== exercise.interval);

  const renderValue = (
    label: string,
    newValue: string | number | undefined,
    originalValue?: string | number | undefined,
  ) => {
    const hasChange = hasOriginal && newValue !== originalValue;
    return (
      <span
        className={`flex items-center gap-1 ${
          hasChange ? "text-green-600 dark:text-green-400" : ""
        }`}
      >
        <span className="font-semibold">{label}:</span>
        {hasOriginal &&
        originalValue !== undefined &&
        originalValue !== newValue ? (
          <>
            <span className="text-gray-400 line-through dark:text-gray-500">
              {originalValue}
            </span>
            <span className="mx-1">→</span>
          </>
        ) : null}
        <span>{newValue || "-"}</span>
      </span>
    );
  };

  return (
    <div
      className={`group rounded-xl border p-2.5 sm:p-3 transition ${
        hasChanges
          ? "border-green-300 bg-green-50/50 dark:border-green-700 dark:bg-green-900/10"
          : "border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-900/10"
      } hover:shadow-sm dark:hover:bg-opacity-20`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {hasOriginal && originalExercise.name !== exercise.name ? (
            <div className="flex flex-col gap-0.5 sm:gap-1">
              <div className="text-xs sm:text-sm text-gray-400 line-through dark:text-gray-500 truncate">
                {originalExercise.name || "Exercício sem nome"}
              </div>
              <div className="text-sm sm:text-base font-medium text-green-600 dark:text-green-400 truncate">
                {exercise.name || "Exercício sem nome"}
              </div>
            </div>
          ) : (
            <div
              className={`text-sm sm:text-base font-medium truncate ${
                hasChanges
                  ? "text-green-600 dark:text-green-400"
                  : "text-gray-900 dark:text-white"
              }`}
            >
              {exercise.name || "Exercício sem nome"}
            </div>
          )}
          <div className="mt-1 sm:mt-1.5 flex flex-wrap gap-2 sm:gap-3 text-xs text-gray-600 dark:text-gray-400">
            {renderValue(
              "Séries",
              exercise.sets || exercise.series,
              originalExercise?.sets || originalExercise?.series,
            )}
            {renderValue(
              "Reps",
              exercise.reps || exercise.repetitions,
              originalExercise?.reps || originalExercise?.repetitions,
            )}
            {renderValue("Carga", exercise.load, originalExercise?.load)}
            {renderValue("Int", exercise.interval, originalExercise?.interval)}
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {hasChanges && (
            <span className="rounded-full bg-green-100 px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Novo
            </span>
          )}
          {showNotRecommendButton && exercise.id && onNotRecommend && (
            <div className="flex items-center gap-1 opacity-100 sm:opacity-0 transition-opacity group-hover:opacity-100">
              {allowForAll && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        "Não recomendar este exercício para TODOS os alunos?",
                      )
                    ) {
                      onNotRecommend(exercise.id!, true);
                    }
                  }}
                  className="rounded-lg p-1 sm:p-1.5 text-red-500 transition-all hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
                  title="Não recomendar para todos os alunos"
                >
                  <FiUsers size={14} className="sm:w-4 sm:h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNotRecommend(exercise.id!, false);
                }}
                className="rounded-lg p-1 sm:p-1.5 text-orange-500 transition-all hover:bg-orange-50 hover:text-orange-600 dark:text-orange-400 dark:hover:bg-orange-900/20"
                title="Não recomendar mais este exercício para este aluno"
              >
                <FiX size={14} className="sm:w-4 sm:h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
